export * from './api.core';
export * from './auth.api';
export * from './portfolio.api';
export * from './lease.api';
export * from './payments.api';
export * from './chat.api';
export * from './expenses.api';
export * from './signing.api';
export * from './lease-amendment.api';
export * from './attention.api';
export * from './account.api';
export * from './move-in.api';
export * from './condition.api';

// ===========================================================================
// Rent Increase API (lease-lifecycle — The Rent Increase Process)
//
// Client functions for the manager rent-increase endpoints
// (apps/api/src/routes/manager/rent-increase.routes.ts). Types are DERIVED
// (z.infer) from @resida/api-types/lease-core's Zod schemas — the same
// schema objects the boundary route registers — rather than hand-redeclared,
// per this layer's api-types-derived convention.
//
// NOTE: this file is normally a pure barrel (`export * from './<domain>.api'`)
// with each domain's client functions living in its own `<domain>.api.ts`
// module. This slice's <scope> restricted edits to the files enumerated in
// its compiled plan, which does not include a new `rent-increase.api.ts` —
// so these functions are defined inline here instead of following that
// per-domain-file convention. Flagged for a follow-up to extract into
// `rent-increase.api.ts` + a barrel re-export, matching every sibling domain.
// ===========================================================================

import { apiFetch } from './api.core';
import type {
  RentIncreaseResponse,
  RentIncreaseStepResponse,
  InitiateRentIncreaseBody,
  UpdateRentIncreaseBody,
  RentIncreasePreviewBody,
  RentIncreasePreview,
  RentIncreaseCandidatesResponse,
  CompleteStepBody,
} from '@resida/api-types/lease-core';

export type {
  RentIncreaseResponse,
  RentIncreaseStepResponse,
  RentIncreaseWarning,
  RentIncreasePreview,
  RentIncreaseCandidate,
  InitiateRentIncreaseBody,
  UpdateRentIncreaseBody,
  CompleteStepBody,
} from '@resida/api-types/lease-core';

export interface RentIncreaseListResponse {
  rentIncreases: RentIncreaseResponse[];
}

/** POST /landlord/api/leases/:leaseId/rent-increases */
export async function initiateRentIncrease(
  leaseId: string,
  body: InitiateRentIncreaseBody
): Promise<RentIncreaseResponse> {
  return apiFetch<RentIncreaseResponse>(`/landlord/api/leases/${leaseId}/rent-increases`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * POST /landlord/api/leases/:leaseId/rent-increases/preview — read-only plan preview (BR-LL-37):
 * steps, due dates, warnings and recipients for a candidate amount/date, before anything is created.
 */
export async function previewRentIncrease(
  leaseId: string,
  body: RentIncreasePreviewBody,
  signal?: AbortSignal
): Promise<RentIncreasePreview> {
  return apiFetch<RentIncreasePreview>(`/landlord/api/leases/${leaseId}/rent-increases/preview`, {
    method: 'POST',
    body: JSON.stringify(body),
    signal,
  });
}

/** GET /landlord/api/rent-increases/candidates — active leases with no rent increase in progress (BR-LL-39) */
export async function listRentIncreaseCandidates(): Promise<RentIncreaseCandidatesResponse> {
  return apiFetch<RentIncreaseCandidatesResponse>('/landlord/api/rent-increases/candidates');
}

/** GET /landlord/api/leases/:leaseId/rent-increases */
export async function listRentIncreasesForLease(leaseId: string): Promise<RentIncreaseListResponse> {
  return apiFetch<RentIncreaseListResponse>(`/landlord/api/leases/${leaseId}/rent-increases`);
}

/** GET /landlord/api/rent-increases */
export async function listRentIncreasesForPortfolio(): Promise<RentIncreaseListResponse> {
  return apiFetch<RentIncreaseListResponse>('/landlord/api/rent-increases');
}

/** GET /landlord/api/rent-increases/:id */
export async function getRentIncrease(id: string): Promise<RentIncreaseResponse> {
  return apiFetch<RentIncreaseResponse>(`/landlord/api/rent-increases/${id}`);
}

/** PATCH /landlord/api/rent-increases/:id — edit a DRAFT (409 once a step document exists or it left DRAFT) */
export async function updateRentIncrease(
  id: string,
  body: UpdateRentIncreaseBody
): Promise<RentIncreaseResponse> {
  return apiFetch<RentIncreaseResponse>(`/landlord/api/rent-increases/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/** POST /landlord/api/rent-increases/:id/activate — DRAFT -> ACTIVE, makes steps actionable */
export async function activateRentIncrease(id: string): Promise<RentIncreaseResponse> {
  return apiFetch<RentIncreaseResponse>(`/landlord/api/rent-increases/${id}/activate`, {
    method: 'POST',
  });
}

/** DELETE /landlord/api/rent-increases/:id */
export async function cancelRentIncrease(id: string, reason?: string): Promise<RentIncreaseResponse> {
  return apiFetch<RentIncreaseResponse>(`/landlord/api/rent-increases/${id}`, {
    method: 'DELETE',
    body: JSON.stringify({ reason }),
  });
}

/** POST /landlord/api/rent-increases/:id/steps/:stepId/generate-document */
export async function generateRentIncreaseStepDocument(
  id: string,
  stepId: string
): Promise<RentIncreaseStepResponse> {
  return apiFetch<RentIncreaseStepResponse>(
    `/landlord/api/rent-increases/${id}/steps/${stepId}/generate-document`,
    { method: 'POST' }
  );
}

/** POST /landlord/api/rent-increases/:id/steps/:stepId/complete */
export async function completeRentIncreaseStep(
  id: string,
  stepId: string,
  body: CompleteStepBody
): Promise<RentIncreaseStepResponse> {
  return apiFetch<RentIncreaseStepResponse>(
    `/landlord/api/rent-increases/${id}/steps/${stepId}/complete`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );
}

/** POST /landlord/api/rent-increases/:id/steps/:stepId/send-email — MANUAL mode only (BR-LL-35); no body */
export async function sendRentIncreaseStepEmail(
  id: string,
  stepId: string
): Promise<RentIncreaseStepResponse> {
  return apiFetch<RentIncreaseStepResponse>(
    `/landlord/api/rent-increases/${id}/steps/${stepId}/send-email`,
    { method: 'POST' }
  );
}
